import React, { MouseEvent, useMemo, useState } from 'react';
import { ClickAwayListener, Dialog, PaperProps } from '@mui/material';
import Draggable from 'react-draggable';
import ShowTooltipIcon from 'spw-static/images/tooltip-icon.svg';
import { Z_INDEX_HIGHER_THAN_BREADCRUMBS } from 'spw-constants';
import { TooltipButton } from './icons';
import { DraggablePaper } from './draggable-paper';

type Props = {
  height: number;
  width: number;
  children: JSX.Element;
  placement?: 'top-left' | 'top-right' | 'bottom-right' | 'bottom-left';
  button?: JSX.Element;
};

const PaperComponent = ({
  onClickAway,
  ...props
}: PaperProps & { onClickAway: () => void }) => {
  return (
    <ClickAwayListener onClickAway={onClickAway}>
      <Draggable bounds="#react-container" handle="#draggable-dialog-title">
        <DraggablePaper {...props} />
      </Draggable>
    </ClickAwayListener>
  );
};

const useDefaultPosition = (
  anchorEl: Element,
  height: number,
  width: number,
  placement: Props['placement']
) =>
  useMemo(() => {
    let top = 0;
    let left = 0;

    const anchorRect = anchorEl?.getBoundingClientRect();

    if (!anchorRect)
      return {
        top,
        left,
      };

    const [vertical, horizontal] = placement.split('-');

    if (vertical === 'top') {
      if (anchorRect.top > height) {
        top = anchorRect.top - height;
      } else {
        top = 0;
      }
    } else if (anchorRect.bottom + height > window.innerHeight) {
      top = window.innerHeight - height;
    } else {
      top = anchorRect.bottom;
    }

    if (horizontal === 'left') {
      left = anchorRect.left - width;
    } else {
      left = anchorRect.right;
    }

    return {
      top,
      left,
    };
  }, [anchorEl, width, height, placement]);

const ClickAwayTooltip = ({
  children,
  height,
  width,
  placement = 'top-left',
  button,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement>();
  const { top, left } = useDefaultPosition(anchorEl, height, width, placement);

  const handleClose = () => {
    setAnchorEl(undefined);
  };
  const handleOpen = (e: MouseEvent<HTMLDivElement>) => {
    setAnchorEl(e.currentTarget);
  };

  return (
    <>
      {button ? (
        <TooltipButton onClick={handleOpen}>{button}</TooltipButton>
      ) : (
        <TooltipButton theme={{ show: !!anchorEl }} onClick={handleOpen}>
          <img alt="tooltip-icon" src={ShowTooltipIcon} />
        </TooltipButton>
      )}

      {anchorEl && (
        <Dialog
          open
          onClose={handleClose}
          PaperComponent={PaperComponent}
          aria-labelledby="draggable-dialog-title"
          style={{
            width: 0,
            height: 0,
          }}
          disableEnforceFocus
          disableScrollLock
          hideBackdrop
          PaperProps={
            {
              style: {
                zIndex: Z_INDEX_HIGHER_THAN_BREADCRUMBS,
                top,
                left,
              },
              onClickAway: handleClose,
            } as PaperProps
          }
          BackdropProps={{
            style: {
              backgroundColor: 'unset',
            },
          }}
        >
          {children}
        </Dialog>
      )}
    </>
  );
};

export default ClickAwayTooltip;
